import { useState } from 'react'

import {
  CAMPAIGN_AUDIENCE_SCOPES,
  CAMPAIGN_SCOPES,
  CAMPAIGN_SOURCE_SCOPES,
  CAMPAIGN_TRIGGERS,
} from '#/entities/campaign'
import type {
  CampaignAudienceScope,
  CampaignScope,
  CampaignSourceScope,
  CampaignTrigger,
} from '#/entities/campaign'

/** Where the answers start — a fresh dialog, or a saved campaign's own. */
export interface CampaignEnrolmentSeed {
  scope?: CampaignScope
  userIds?: string[]
  trigger?: CampaignTrigger
  providerIds?: string[]
}

/** The seed filled out with the fresh-dialog defaults. */
function openingAnswers(seed: CampaignEnrolmentSeed) {
  const userIds = seed.userIds ?? []
  const providerIds = seed.providerIds ?? []
  return {
    scope: seed.scope ?? CAMPAIGN_SCOPES.organization,
    audience:
      userIds.length > 0
        ? CAMPAIGN_AUDIENCE_SCOPES.pickUsers
        : CAMPAIGN_AUDIENCE_SCOPES.allUsers,
    userIds,
    trigger: seed.trigger ?? CAMPAIGN_TRIGGERS.whenQuoted,
    sources:
      providerIds.length > 0
        ? CAMPAIGN_SOURCE_SCOPES.pickSources
        : CAMPAIGN_SOURCE_SCOPES.allSources,
    providerIds,
  }
}

/**
 * Ownership and trigger state, shared by every enrolment modal.
 *
 * They all ask the same questions in the same words, so they ask them through
 * the same hook — the only difference is what each one does with the answers.
 * "Pick users" and "Pick sources" carry the ids they picked; the create body
 * reads them only when the matching chip is the active one. A seed opens the
 * dialog on a campaign's saved answers; the chips follow from the ids (an id
 * list means "pick", an empty one means "all").
 */
export function useCampaignEnrolment(seed: CampaignEnrolmentSeed = {}) {
  const start = openingAnswers(seed)
  const [scope, setScope] = useState<CampaignScope>(start.scope)
  const [audience, setAudience] = useState<CampaignAudienceScope>(
    start.audience,
  )
  const [userIds, setUserIds] = useState<string[]>(start.userIds)
  const [trigger, setTrigger] = useState<CampaignTrigger>(start.trigger)
  const [sources, setSources] = useState<CampaignSourceScope>(start.sources)
  const [providerIds, setProviderIds] = useState<string[]>(start.providerIds)

  const isOrganization = scope === CAMPAIGN_SCOPES.organization
  const picksUsers =
    isOrganization && audience === CAMPAIGN_AUDIENCE_SCOPES.pickUsers
  const picksSources = sources === CAMPAIGN_SOURCE_SCOPES.pickSources

  return {
    scope,
    setScope,
    audience,
    setAudience,
    userIds,
    setUserIds,
    trigger,
    setTrigger,
    sources,
    setSources,
    providerIds,
    setProviderIds,
    isOrganization,
    picksUsers,
    picksSources,
    /** What the create body should carry — empty when the chip says "all". */
    appliesToUserIds: picksUsers ? userIds : [],
    selectedProviderIds: picksSources ? providerIds : [],
  }
}

export type UseCampaignEnrolmentResult = ReturnType<typeof useCampaignEnrolment>
